import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import type { Project } from '@/types'
import { mockProjects } from '@/data/projects'
import {
  projectDefinationApi,
  type ProjectDefinitionItem,
  type ProjectDefinitionResponse,
} from '@/services/projectDefinitions'
import {
  createProjectApi,
  updateProjectApi,
  deleteProjectApi,
  type CreateProjectPayload,
  type UpdateProjectPayload,
} from '@/services/projects'

export interface ProjectsState {
  items: Project[]
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  error: string | null
  mutating: boolean
  mutationError: string | null
}

const initialState: ProjectsState = {
  items: [],
  status: 'idle',
  error: null,
  mutating: false,
  mutationError: null,
}

function hashToPositiveInt(value: string, fallbackSeed: number): number {
  let hash = 0
  const input = value || `${fallbackSeed}`

  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0
  }

  return hash || fallbackSeed + 1
}

function mapProjectDefinitionToTile(
  project: ProjectDefinitionItem,
  index: number
): Project {
  const template = mockProjects[index % mockProjects.length]
  const stableId = hashToPositiveInt(
    project._id ?? project.project_name ?? `${index}`,
    index
  )

  return {
    id: stableId,
    _id: project._id,
    name: project.project_name ?? template.name,
    description: project.metadata?.description ?? template.description,
    createdAt: project.created_at,
    progress: template.progress,
    status: project.status ?? template.status,
    updatedAt: template.updatedAt,
    language: template.language,
    target: template.target,
    files: template.files,
    complexity: template.complexity,
    priority: project.priority,
    userAccess: project.user_access,
  }
}

function extractProjects(
  data?: ProjectDefinitionResponse
): ProjectDefinitionItem[] {
  if (Array.isArray(data?.projects)) {
    return data.projects
  }
  return []
}

export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (_, { rejectWithValue }) => {
    const response = await projectDefinationApi()

    if (!response.success) {
      return rejectWithValue(response.message ?? 'Unable to fetch projects')
    }

    const rawProjects = extractProjects(response.data)
    return rawProjects.map(mapProjectDefinitionToTile)
  }
)

export const createProject = createAsyncThunk(
  'projects/createProject',
  async (payload: CreateProjectPayload, { rejectWithValue }) => {
    const response = await createProjectApi(payload)
    if (!response.success) {
      return rejectWithValue(response.message ?? 'Unable to create project')
    }
    return response.data as ProjectDefinitionItem
  }
)

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async (
    { id, payload }: { id: string; payload: UpdateProjectPayload },
    { rejectWithValue }
  ) => {
    const response = await updateProjectApi(id, payload)
    if (!response.success) {
      return rejectWithValue(response.message ?? 'Unable to update project')
    }
    return { id, payload }
  }
)

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (id: string, { rejectWithValue }) => {
    const response = await deleteProjectApi(id)
    if (!response.success) {
      return rejectWithValue(response.message ?? 'Unable to delete project')
    }
    return id
  }
)

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchProjects.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.items = action.payload
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.status = 'failed'
        state.error =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Unable to fetch projects'
      })
      // Create
      .addCase(createProject.pending, (state) => {
        state.mutating = true
        state.mutationError = null
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.mutating = false
        const raw = action.payload as ProjectDefinitionItem
        const newProject = mapProjectDefinitionToTile(raw, state.items.length)
        state.items.push(newProject)
      })
      .addCase(createProject.rejected, (state, action) => {
        state.mutating = false
        state.mutationError = (action.payload as string | undefined) ?? action.error.message ?? 'Unable to create project'
      })
      // Update
      .addCase(updateProject.pending, (state) => {
        state.mutating = true
        state.mutationError = null
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.mutating = false
        const { id, payload } = action.payload
        const project = state.items.find((p) => p._id === id)
        if (!project) return
        if (payload.metadata?.description !== undefined) {
          project.description = payload.metadata.description as string
        }
        if (payload.priority !== undefined) project.priority = payload.priority
        if (payload.status !== undefined) project.status = payload.status
        if (payload.user_access !== undefined) project.userAccess = payload.user_access
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.mutating = false
        state.mutationError = (action.payload as string | undefined) ?? action.error.message ?? 'Unable to update project'
      })
      // Delete — remove by _id (MongoDB string id)
      .addCase(deleteProject.pending, (state) => {
        state.mutating = true
        state.mutationError = null
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.mutating = false
        const deletedId = action.payload
        state.items = state.items.filter((p) => p._id !== deletedId)
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.mutating = false
        state.mutationError = (action.payload as string | undefined) ?? action.error.message ?? 'Unable to delete project'
      })
  },
})

export default projectsSlice.reducer
